import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { saveToCache, readFromCache } from "../../../services/pathologyCache";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { FaPrint, FaTrash, FaMoneyBillWave, FaUndo } from "react-icons/fa";
import ExportOptions from "../../ExportOptions";
import TableSkeleton from "../../TableSkeleton";
import PageHeading from "../../PageHeading";
import DynamicSelect from "../../DynamicSelect";
import { NicuPrintLayout } from "../../NicuPrintLayout";

const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";
const ADVANCE_URL = "https://api.muktihospital.com/api/nicu/advance";
const ADMISSION_URL = "https://api.muktihospital.com/api/nicu/admission";

const filterOptions = [
  { value: "monthly", label: "Monthly" },
  { value: "daily", label: "Daily" },
  { value: "yearly", label: "Yearly" },
  { value: "all", label: "All Time" },
];

const statusOptions = [
  { value: "all", label: "All" },
  { value: "unused", label: "Unused" },
  { value: "used", label: "Used" },
  { value: "refunded", label: "Refunded" },
];

const numberToWordsBD = (value) => {
  let num = Math.floor(Number(value) || 0);
  if (num === 0) return "Zero";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const two = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : ""));
  const three = (n) => { const h = Math.floor(n / 100); const r = n % 100; return (h ? ones[h] + " Hundred" + (r ? " " : "") : "") + (r ? two(r) : ""); };
  let words = "";
  const crore = Math.floor(num / 10000000); num %= 10000000;
  const lakh = Math.floor(num / 100000); num %= 100000;
  const thousand = Math.floor(num / 1000); num %= 1000;
  if (crore) words += three(crore) + " Crore ";
  if (lakh) words += two(lakh) + " Lakh ";
  if (thousand) words += two(thousand) + " Thousand ";
  if (num) words += three(num);
  return words.trim();
};

const NicuAdvancePayment = () => {
  const [advances, setAdvances] = useState([]);
  const [summary, setSummary] = useState({ totalAdvance: 0, totalUsed: 0, totalUnused: 0, count: 0 });
  const [nicuOfflineActive, setNicuOfflineActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundNote, setRefundNote] = useState("");

  const [filterType, setFilterType] = useState("monthly");
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const [admittedPatients, setAdmittedPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [form, setForm] = useState({
    regNo: "",
    patientName: "",
    phoneNumber: "",
    amount: "",
    paymentDate: new Date().toISOString().split("T")[0],
    note: "",
  });

  const [receipt, setReceipt] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);

  const itemsPerPage = 10;

  const fetchAdvances = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filterType === "daily") { params.startDate = startDate; params.endDate = endDate; }
    else if (filterType === "monthly") { const [y, m] = selectedMonth.split("-"); params.year = y; params.month = m; }
    else if (filterType === "yearly") { params.year = selectedYear; }
    if (statusFilter !== "all") params.status = statusFilter;

    const cacheKey = `nicu_advances_${filterType}_${statusFilter}_${JSON.stringify(params)}`;

    try {
      const res = await axios.get(ADVANCE_URL, { headers: { "x-api-key": API_KEY }, params });
      setAdvances(res.data.advances || []);
      setSummary(res.data.summary || { totalAdvance: 0, totalUsed: 0, totalUnused: 0, count: 0 });

      // Cache this filter for offline viewing.
      setNicuOfflineActive(false);
      saveToCache(cacheKey, {
        advances: res.data.advances || [],
        summary: res.data.summary || { totalAdvance: 0, totalUsed: 0, totalUnused: 0, count: 0 },
      });
    } catch (err) {
      console.error("Error loading advances:", err);
      if (err.code === "ERR_NETWORK" || !navigator.onLine) {
        const cached = await readFromCache(cacheKey);
        if (cached && cached.advances) {
          setAdvances(cached.advances);
          setSummary(cached.summary || { totalAdvance: 0, totalUsed: 0, totalUnused: 0, count: 0 });
          setNicuOfflineActive(true);
          toast.info("Offline: showing saved advance payments.");
        } else {
          setAdvances([]);
          setNicuOfflineActive(false);
          toast.info("Offline: this filter isn't saved yet. Open it online once.");
        }
      } else {
        toast.error("Failed to load advance payments.");
        setAdvances([]);
      }
    } finally {
      setLoading(false);
    }
  }, [filterType, statusFilter, startDate, endDate, selectedMonth, selectedYear]);

  useEffect(() => { fetchAdvances(); }, [fetchAdvances]);

  // Advance is taken only from a patient who is already admitted, so the
  // dropdown is built from live admissions — no name or phone is typed by hand.
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(ADMISSION_URL, {
          headers: { "x-api-key": API_KEY },
          params: { limit: 500 },
        });
        const list = res.data.data?.admissions || [];
        setAdmittedPatients(
          list
            .filter((a) => a.status === "Admitted")
            .map((a) => ({
              value: a.regNo,
              label: `${a.patientName || "Unknown"} — ${a.regNo || ""}`,
              patientName: a.patientName || "",
              regNo: a.regNo || "",
              phoneNumber: a.phoneNumber || a.relativeMobileNumber || "",
            }))
        );
      } catch (err) {
        console.error("Failed to load admitted patients:", err);
        toast.error("Failed to load the admitted patient list.");
      }
    })();
  }, []);

  // Print trigger
  useEffect(() => {
    if (showReceipt && receipt) {
      const timer = setTimeout(() => {
        window.print();
        setTimeout(() => {
          setShowReceipt(false);
          setReceipt(null);
        }, 500);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [showReceipt, receipt]);

  const resetForm = () => {
    setSelectedPatient(null);
    setForm({
      regNo: "", patientName: "", phoneNumber: "", amount: "",
      paymentDate: new Date().toISOString().split("T")[0], note: "",
    });
  };

  const handleReceive = async (thenPrint) => {
    if (!form.regNo) { toast.error("Select an admitted patient first."); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error("Enter a valid amount."); return; }

    try {
      setSaving(true);
      const res = await axios.post(`${ADVANCE_URL}/add`, {
        regNo: form.regNo,
        patientName: form.patientName.trim(),
        phoneNumber: form.phoneNumber.trim(),
        amount: parseFloat(form.amount),
        paymentDate: form.paymentDate,
        note: form.note || null,
      }, { headers: { "x-api-key": API_KEY } });

      const saved = res.data.advance || res.data;
      toast.success("Advance payment received!");
      resetForm();
      fetchAdvances();
      if (thenPrint) { setReceipt(saved); setShowReceipt(true); }
    } catch (err) {
      console.error("Failed to receive advance:", err);
      toast.error(err.response?.data?.error || "Failed to receive advance.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${ADVANCE_URL}/delete/${deleteId}`, { headers: { "x-api-key": API_KEY } });
      toast.success("Advance deleted.");
      setDeleteId(null);
      fetchAdvances();
    } catch (err) {
      console.error("Delete failed:", err);
      toast.error(err.response?.data?.error || "Failed to delete.");
      setDeleteId(null);
    }
  };

  const handleRefund = async () => {
    if (!refundTarget) return;
    try {
      await axios.post(`${ADVANCE_URL}/refund/${refundTarget.id}`,
        { refundNote: refundNote || null },
        { headers: { "x-api-key": API_KEY } });
      toast.success(`৳${refundTarget.amount} refunded.`);
      setRefundTarget(null);
      setRefundNote("");
      fetchAdvances();
    } catch (err) {
      console.error("Refund failed:", err);
      toast.error(err.response?.data?.error || "Failed to refund.");
      setRefundTarget(null);
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return advances.filter((a) =>
      (a.patientName || "").toLowerCase().includes(q) ||
      (a.phoneNumber || "").toLowerCase().includes(q) ||
      (a.regNo || "").toLowerCase().includes(q) ||
      (a.id || "").toLowerCase().includes(q)
    );
  }, [advances, searchQuery]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const currentData = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "-");

  const headers = ["Receipt No", "Reg No", "Patient", "Phone", "Amount", "Date", "Status"];
  const dataMapper = (a) => [a.id, a.regNo || "-", a.patientName, a.phoneNumber, a.amount, fmtDate(a.paymentDate), a.isUsed ? "Used" : a.isRefunded ? "Refunded" : "Unused"].join("\t");
  const breadcrumbs = useMemo(() => [{ label: "Dashboard", url: "/" }, { label: "NICU" }, { label: "Advance Payment" }], []);

  return (
    <div>
      <ToastContainer />
      <div className="print-hidden"><PageHeading title="NICU Advance Payment" breadcrumbs={breadcrumbs} /></div>
      {nicuOfflineActive && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-orange-50 border border-orange-200 text-orange-800 text-sm flex items-center gap-2 print-hidden">
          <span>⚠️</span>
          <span>Offline mode — showing saved data. Connect to the internet for the latest.</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4 print-hidden">
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
          <p className="text-xs text-gray-500">Total Advance</p>
          <p className="text-xl font-black text-blue-600">৳{Math.round(summary.totalAdvance)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-green-500">
          <p className="text-xs text-gray-500">Used in Bills</p>
          <p className="text-xl font-black text-green-600">৳{Math.round(summary.totalUsed)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-amber-500">
          <p className="text-xs text-gray-500">Unused (in hand)</p>
          <p className="text-xl font-black text-amber-600">৳{Math.round(summary.totalUnused)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-red-500">
          <p className="text-xs text-gray-500">Refunded</p>
          <p className="text-xl font-black text-red-600">৳{Math.round(summary.totalRefunded || 0)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-gray-400">
          <p className="text-xs text-gray-500">Total Receipts</p>
          <p className="text-xl font-black text-gray-700">{summary.count}</p>
        </div>
      </div>

      {/* Receive form */}
      <div className="bg-white shadow-sm rounded-lg mb-4 print-hidden border-l-4 border-[#73CB64]">
        <div className="p-4 border-b border-dashed border-M-text-color/50 flex items-center gap-2">
          <FaMoneyBillWave className="text-[#73CB64]" />
          <h2 className="text-base font-medium text-gray-700">Receive Advance Payment</h2>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-xs font-bold block mb-1">Admitted Patient *</label>
              <DynamicSelect
                options={admittedPatients}
                value={selectedPatient}
                onChange={(opt) => {
                  setSelectedPatient(opt);
                  setForm((prev) => ({
                    ...prev,
                    regNo: opt?.regNo || "",
                    patientName: opt?.patientName || "",
                    phoneNumber: opt?.phoneNumber || "",
                  }));
                }}
                isSearchable
                isClearable
                placeholder="Search patient or Reg No"
              />
              {form.regNo && (
                <p className="text-xs text-gray-400 mt-1">Reg No: {form.regNo}</p>
              )}
            </div>
            <div>
              <label className="text-xs font-bold block mb-1">Phone Number</label>
              <input type="text" value={form.phoneNumber} readOnly
                placeholder="Auto from admission"
                className="w-full border p-2 rounded text-sm bg-gray-100 outline-none" />
            </div>
            <div>
              <label className="text-xs font-bold block mb-1">Amount (৳) *</label>
              <input type="number" placeholder="0.00" value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-xs font-bold block mb-1">Date</label>
              <input type="date" value={form.paymentDate}
                onChange={(e) => setForm({ ...form, paymentDate: e.target.value })}
                className="w-full border p-2 rounded text-sm" />
            </div>
            <div>
              <label className="text-xs font-bold block mb-1">Note</label>
              <input type="text" placeholder="Optional note" value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                className="w-full border p-2 rounded text-sm focus:border-[#73CB64] outline-none" />
            </div>
          </div>
          <p className="text-xs text-gray-400 mb-3">This advance is tied to the patient&apos;s Reg No and fills in automatically when their NICU bill is made.</p>
          <div className="flex justify-end gap-3">
            <button onClick={() => handleReceive(false)} disabled={saving}
              className="bg-gray-600 text-white text-sm py-2 px-5 rounded-md hover:bg-gray-700 disabled:opacity-50">
              {saving ? "Saving..." : "Receive Advance"}
            </button>
            <button onClick={() => handleReceive(true)} disabled={saving}
              className="bg-[#73CB64] text-white text-sm py-2 px-5 rounded-md hover:bg-M-primary-color flex items-center gap-1.5 disabled:opacity-50">
              <FaPrint size={12} /> Receive &amp; Print
            </button>
          </div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <h2 className="text-base font-medium text-gray-700">Advance Payments</h2>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="w-32">
              <DynamicSelect options={filterOptions} value={filterOptions.find((o) => o.value === filterType)} onChange={(o) => setFilterType(o.value)} isSearchable={false} placeholder="Filter" />
            </div>
            <div className="w-28">
              <DynamicSelect options={statusOptions} value={statusOptions.find((o) => o.value === statusFilter)} onChange={(o) => setStatusFilter(o.value)} isSearchable={false} placeholder="Status" />
            </div>
            {filterType === "daily" && (
              <>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="p-2 border rounded-md text-sm w-32" />
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="p-2 border rounded-md text-sm w-32" />
              </>
            )}
            {filterType === "monthly" && <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="p-2 border rounded-md text-sm" />}
            {filterType === "yearly" && <input type="number" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="p-2 border rounded-md text-sm w-24" min="2000" max="2100" />}
            <input type="text" placeholder="Search name/phone..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="max-w-48 w-full p-2 border rounded-md text-sm" />
            <ExportOptions data={currentData} dropdownOpen={dropdownOpen} toggleDropdown={() => setDropdownOpen(!dropdownOpen)} title="NICU Advance Payments" headers={headers} dataMapper={dataMapper} />
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <table className="min-w-[800px] w-full"><TableSkeleton rowCount={itemsPerPage} columns={[{ width: "90px" }, { width: "140px" }, { width: "120px" }, { width: "90px" }, { width: "100px" }, { width: "90px" }, { width: "90px" }]} /></table>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-gray-500">No advance payments found.</div>
          ) : (
            <table className="min-w-[800px] w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Receipt No</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Reg No</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Patient</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Phone</th>
                  <th className="p-3 text-right text-sm font-medium text-gray-700">Amount (৳)</th>
                  <th className="p-3 text-left text-sm font-medium text-gray-700">Date</th>
                  <th className="p-3 text-center text-sm font-medium text-gray-700">Status</th>
                  <th className="p-3 text-center text-sm font-medium text-gray-700">Action</th>
                </tr>
              </thead>
              <tbody>
                {currentData.map((a) => (
                  <tr key={a.id} className="border-b hover:bg-gray-50">
                    <td className="p-3 text-sm text-gray-500">{a.id}</td>
                    <td className="p-3 text-sm text-gray-600">{a.regNo || "-"}</td>
                    <td className="p-3 text-sm text-gray-700">{a.patientName}</td>
                    <td className="p-3 text-sm text-gray-600">{a.phoneNumber}</td>
                    <td className="p-3 text-sm text-right font-bold text-blue-600">৳{a.amount}</td>
                    <td className="p-3 text-sm text-gray-500">{fmtDate(a.paymentDate)}</td>
                    <td className="p-3 text-center">
                      {a.isUsed ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700" title={a.billNumber || ""}>Used</span>
                      ) : a.isRefunded ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700" title={a.refundNote || ""}>Refunded</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">Unused</span>
                      )}
                    </td>
                    <td className="p-3">
                      {(() => {
                        const locked = a.isUsed || a.isRefunded;
                        const lockReason = a.isUsed
                          ? "Already used in a bill"
                          : "Already refunded";
                        return (
                          <div className="flex items-center justify-center gap-2">
                            <button
                              onClick={() => { setReceipt(a); setShowReceipt(true); }}
                              className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                              title="Print receipt"
                            >
                              <FaPrint size={12} />
                            </button>
                            <button
                              onClick={() => setRefundTarget(a)}
                              disabled={locked}
                              className={`size-7 rounded-full text-sm inline-flex items-center justify-center transition-all duration-300 ${
                                locked
                                  ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                  : "bg-amber-500/10 text-amber-600 hover:bg-amber-500 hover:text-white"
                              }`}
                              title={locked ? lockReason : "Refund this advance"}
                            >
                              <FaUndo size={12} />
                            </button>
                            <button
                              onClick={() => setDeleteId(a.id)}
                              disabled={locked}
                              className={`size-7 rounded-full text-sm inline-flex items-center justify-center transition-all duration-300 ${
                                locked
                                  ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                  : "bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white"
                              }`}
                              title={locked ? lockReason : "Delete this advance"}
                            >
                              <FaTrash size={12} />
                            </button>
                          </div>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="p-4 flex justify-end gap-2 print-hidden">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)} className="p-2 border rounded disabled:opacity-50"><MdKeyboardArrowLeft /></button>
            <span className="p-2 text-sm">Page {currentPage} of {totalPages}</span>
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage((p) => p + 1)} className="p-2 border rounded disabled:opacity-50"><MdKeyboardArrowRight /></button>
          </div>
        )}
      </div>

      {/* Refund confirm */}
      {refundTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-5">
            <h3 className="text-base font-bold text-gray-700 mb-1">Refund this advance?</h3>
            <p className="text-sm text-gray-500 mb-3">
              ৳{refundTarget.amount} will be given back to {refundTarget.patientName} ({refundTarget.phoneNumber}).
            </p>
            <label className="text-xs font-bold block mb-1">Reason (optional)</label>
            <input type="text" value={refundNote} onChange={(e) => setRefundNote(e.target.value)}
              placeholder="e.g. Patient did not admit"
              className="w-full border p-2 rounded text-sm mb-4 focus:border-[#73CB64] outline-none" />
            <div className="flex justify-end gap-3">
              <button onClick={() => { setRefundTarget(null); setRefundNote(""); }} className="bg-gray-200 text-gray-700 text-sm py-2 px-4 rounded-md hover:bg-gray-300">Cancel</button>
              <button onClick={handleRefund} className="bg-orange-600 text-white text-sm py-2 px-4 rounded-md hover:bg-orange-700">Confirm Refund</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-5">
            <h3 className="text-base font-bold text-gray-700 mb-2">Delete this advance?</h3>
            <p className="text-sm text-gray-500 mb-4">This cannot be undone.</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="bg-gray-200 text-gray-700 text-sm py-2 px-4 rounded-md hover:bg-gray-300">Cancel</button>
              <button onClick={handleDelete} className="bg-red-600 text-white text-sm py-2 px-4 rounded-md hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Printable receipt — shared NICU A4 layout ===== */}
      {showReceipt && receipt && (
        <NicuPrintLayout
          className="adv-receipt"
          title="Advance Payment Receipt"
          subtitle="NICU Department"
          signatures={["Received By", "Authorized By", "Payer's Signature"]}
          amountInWords={`${numberToWordsBD(receipt.amount)} Taka Only`}
          billInfo={
            <div style={{ border: "1px solid #000", padding: "8px 12px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: "20px", rowGap: "4px" }}>
                <p style={{ margin: 0 }}><strong>Receipt No:</strong> {receipt.id}</p>
                <p style={{ margin: 0 }}><strong>Date:</strong> {fmtDate(receipt.paymentDate)}</p>
                <p style={{ margin: 0 }}><strong>Patient Name:</strong> {receipt.patientName}</p>
                <p style={{ margin: 0 }}><strong>Reg No:</strong> {receipt.regNo || "N/A"}</p>
                <p style={{ margin: 0, gridColumn: "1 / -1" }}><strong>Phone Number:</strong> {receipt.phoneNumber || "N/A"}</p>
                {receipt.note && (
                  <p style={{ margin: 0, gridColumn: "1 / -1" }}><strong>Note:</strong> {receipt.note}</p>
                )}
              </div>
            </div>
          }
          table={
            <>
              <thead>
                <tr>
                  <th style={{ width: "8%" }}>Sl</th>
                  <th>Description</th>
                  <th style={{ width: "25%" }}>Amount (৳)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>1</td>
                  <td>Advance Payment Received (NICU)</td>
                  <td>{Number(receipt.amount).toFixed(2)}</td>
                </tr>
              </tbody>
            </>
          }
          summary={
            <>
              <div><span>Advance Received</span><span>৳ {Number(receipt.amount).toFixed(2)}</span></div>
            </>
          }
          note={
            <div>
              <p style={{ margin: 0, fontWeight: "bold" }}>Please keep this receipt.</p>
              <p style={{ margin: "4px 0 0" }}>
                This advance will be adjusted with the NICU bill for the Reg No shown above.
              </p>
            </div>
          }
        />
      )}
    </div>
  );
};

export default NicuAdvancePayment;
